import { useTranslation } from "react-i18next";
import { ClipboardCheck, Coins, Loader2, Radar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCredits } from "@/contexts/CreditsContext";
import type { GewerbegebietZone } from "@/types/api";
import { StepFooter } from "./StepFooter";

interface ReviewStepProps {
  industry: string;
  employeeSize: string;
  location: string;
  searchRadius: number;
  searchMode: "radius" | "zones";
  selectedZones: GewerbegebietZone[];
  keywords: string[];
  excludeKeywords: string[];
  maxResults: number;
  language: string;
  countryCode: string;
  onGoToStep: (step: number) => void;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

interface ReviewRowProps {
  label: string;
  value: string;
  editLabel: string;
  stepNumber: number;
  onGoToStep: (step: number) => void;
}

function ReviewRow({
  label,
  value,
  editLabel,
  stepNumber,
  onGoToStep,
}: ReviewRowProps) {
  return (
    <div className="flex justify-between items-center py-2.5 border-b border-border last:border-b-0">
      <span className="text-[0.8125rem] text-muted-foreground font-medium">
        {label}
      </span>
      <span className="text-sm font-medium text-right">
        {value}
        <button
          type="button"
          className="ml-2 text-xs text-primary underline underline-offset-2 cursor-pointer"
          onClick={() => onGoToStep(stepNumber)}
        >
          {editLabel}
        </button>
      </span>
    </div>
  );
}

export function ReviewStep({
  industry,
  employeeSize,
  location,
  searchRadius,
  searchMode,
  selectedZones,
  keywords,
  excludeKeywords,
  maxResults,
  language,
  countryCode,
  onGoToStep,
  onBack,
  onSubmit,
  isSubmitting,
}: ReviewStepProps) {
  const { t } = useTranslation("search");

  // Module-level const maps moved inside component so entries can call t()
  const SIZE_LABELS: Record<string, string> = {
    any: t("wizard.industryStep.sizes.any"),
    "1-9": t("wizard.industryStep.sizes.micro"),
    "10-49": t("wizard.industryStep.sizes.small"),
    "50-249": t("wizard.industryStep.sizes.medium"),
    "250-999": t("wizard.industryStep.sizes.large"),
    "1000+": t("wizard.industryStep.sizes.enterprise"),
  };

  const LANGUAGE_LABELS: Record<string, string> = {
    de: t("wizard.reviewStep.languages.de"),
    en: t("wizard.reviewStep.languages.en"),
    fr: t("wizard.reviewStep.languages.fr"),
  };

  const COUNTRY_LABELS: Record<string, string> = {
    de: t("wizard.reviewStep.countries.de"),
    at: t("wizard.reviewStep.countries.at"),
    ch: t("wizard.reviewStep.countries.ch"),
    fr: t("wizard.reviewStep.countries.fr"),
    gb: t("wizard.reviewStep.countries.gb"),
  };

  // Derive display values
  const sizeLabel = SIZE_LABELS[employeeSize] ?? "—";
  const industryLabel = industry || "—";

  let locationLabel = "—";
  if (searchMode === "zones" && selectedZones.length > 0) {
    const names = selectedZones.map((z) => z.name);
    if (names.length <= 2) {
      locationLabel = t("wizard.reviewStep.zonesLocation_few", {
        names: names.join(", "),
      });
    } else {
      locationLabel = t("wizard.reviewStep.zonesLocation_many", {
        names: names.slice(0, 2).join(", "),
        extra: names.length - 2,
      });
    }
  } else if (location) {
    locationLabel = t("wizard.reviewStep.radiusLocation", {
      location,
      radius: searchRadius,
    });
  }

  const includeLabel = keywords.length > 0 ? keywords.join(", ") : "—";
  const excludeLabel =
    excludeKeywords.length > 0 ? excludeKeywords.join(", ") : "—";

  const languageLabel = LANGUAGE_LABELS[language] ?? language;
  const countryLabel = COUNTRY_LABELS[countryCode] ?? countryCode;
  const settingsLabel = t("wizard.reviewStep.settingsSummary", {
    count: maxResults,
    language: languageLabel,
    country: countryLabel,
  });

  const editLabel = t("wizard.reviewStep.editButton");

  // Mirror backend cost derivation in main.py:303-311 exactly:
  //   1. search_basic (≤10 results) OR search_extended (>10 results)
  //   2. + gewerbegebiet if zone_bounds is non-empty
  // Values come from action_registry via useCredits — never hardcoded.
  const { costOf, balance } = useCredits();
  const baseCode = maxResults > 10 ? "search_extended" : "search_basic";
  const hasZones = searchMode === "zones" && selectedZones.length > 0;
  const totalCost = costOf(baseCode) + (hasZones ? costOf("gewerbegebiet") : 0);
  const insufficient = totalCost > 0 && balance < totalCost;

  const submitButton = (
    <Button
      size="lg"
      className="w-full max-w-[300px]"
      onClick={onSubmit}
      disabled={isSubmitting}
    >
      {isSubmitting ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t("wizard.reviewStep.startingSearch")}
        </>
      ) : totalCost > 0 ? (
        <>
          <Coins
            className={`mr-2 h-4 w-4 ${insufficient ? "text-destructive" : ""}`}
          />
          <span className={insufficient ? "text-destructive" : ""}>
            {totalCost}
          </span>
          <span className="mx-2 opacity-60">·</span>
          {t("wizard.reviewStep.startSearch")}
        </>
      ) : (
        <>
          <Radar className="mr-2 h-4 w-4" />
          {t("wizard.reviewStep.startSearch")}
        </>
      )}
    </Button>
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            {t("wizard.reviewStep.cardTitle")}
          </CardTitle>
        </CardHeader>

        <CardContent>
          <ReviewRow
            label={t("wizard.reviewStep.labels.industry")}
            value={industryLabel}
            editLabel={editLabel}
            stepNumber={2}
            onGoToStep={onGoToStep}
          />
          <ReviewRow
            label={t("wizard.reviewStep.labels.companySize")}
            value={sizeLabel}
            editLabel={editLabel}
            stepNumber={2}
            onGoToStep={onGoToStep}
          />
          <ReviewRow
            label={t("wizard.reviewStep.labels.location")}
            value={locationLabel}
            editLabel={editLabel}
            stepNumber={3}
            onGoToStep={onGoToStep}
          />
          <ReviewRow
            label={t("wizard.reviewStep.labels.includeKeywords")}
            value={includeLabel}
            editLabel={editLabel}
            stepNumber={4}
            onGoToStep={onGoToStep}
          />
          <ReviewRow
            label={t("wizard.reviewStep.labels.excludeKeywords")}
            value={excludeLabel}
            editLabel={editLabel}
            stepNumber={4}
            onGoToStep={onGoToStep}
          />
          <ReviewRow
            label={t("wizard.reviewStep.labels.settings")}
            value={settingsLabel}
            editLabel={editLabel}
            stepNumber={5}
            onGoToStep={onGoToStep}
          />
        </CardContent>
      </Card>

      <StepFooter onBack={onBack} submitButton={submitButton} />
    </div>
  );
}
